/**
 * @fileoverview Matchmaking Test Suite
 *
 * Automated tests for matchmaking, account management, ratings, and game flows.
 * These tests run against actual Firebase services and verify the data.
 */

import {
  MatchmakingTest,
  MatchmakingTestSuite,
  MatchmakingTestResult,
  IntegrationTest,
  TestSuite,
} from "../types";
import {
  passTest,
  failTest,
  assertEqual,
  assertInRange,
  assertTrue,
  assertDefined,
  AssertionError,
} from "../MatchmakingTestRunner";
import { MMRService } from "../../MMR/MMRService";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";

// Constants (match MMRService private values)
const START_RATING = 1300;
const PLACEMENT_GAMES = 10;
const MAX_EXCHANGE = 30;
const FALLOFF_THRESHOLD = 300;

/**
 * Calculate expected win probability (mirrors MMRService)
 */
function calculateWinProbability(ratingA: number, ratingB: number): number {
  const ratingDiff = ratingA - ratingB;
  return 1 / (1 + Math.pow(10, -ratingDiff / 400));
}

/**
 * Calculate expected placement change (mirrors MMRService)
 */
function calculateExpectedPlacementChange(
  provisionalRating: number,
  opponentRating: number,
  result: "win" | "loss" | "draw",
  gameNumber: number
): number {
  const expectedScore = calculateWinProbability(provisionalRating, opponentRating);
  const actualScore = result === "win" ? 1 : result === "draw" ? 0.5 : 0;
  const volatility = Math.max(100 - (gameNumber - 1) * 6, 40);
  return Math.round(volatility * (actualScore - expectedScore));
}

/**
 * Format rating change with sign
 */
function formatChange(change: number): string {
  return change >= 0 ? `+${change}` : `${change}`;
}

/**
 * Generate random win/loss outcome
 */
function randomOutcome(): "light" | "dark" {
  return Math.random() < 0.5 ? "light" : "dark";
}

/**
 * Detailed game result for display
 */
interface DetailedGameResult {
  gameNumber: number;
  outcome: "win" | "loss" | "draw";
  ratingBefore: number;
  ratingAfter: number;
  change: number;
  expectedChange: number;
  opponentRating: number;
  placementProgress: string;
  isPlaced: boolean;
}

// ============================================================================
// TEST UTILITIES
// ============================================================================

// Track test users for cleanup
const testUsersToCleanup: string[] = [];

/**
 * Generate a unique test user ID and track it for cleanup
 */
function generateTestUserId(): string {
  const id = `test_user_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  testUsersToCleanup.push(id);
  return id;
}

/**
 * Delete a test user from Firebase
 */
async function deleteTestUser(userId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, "users", userId));
  } catch (error) {
    // Silently ignore cleanup errors - test data may already be deleted
  }
}

/**
 * Cleanup all tracked test users
 */
async function cleanupTestUsers(userIds: string[]): Promise<number> {
  let cleaned = 0;
  for (const userId of userIds) {
    await deleteTestUser(userId);
    cleaned++;
  }
  return cleaned;
}

/**
 * Create a mock Firebase User object for testing
 */
function createMockUser(uid: string, displayName: string = "TestUser"): any {
  return {
    uid,
    displayName,
    email: `${uid}@test.com`,
  };
}

/**
 * Create a test helper that wraps the test logic with timing, error handling, and cleanup
 */
function createTest(
  name: string,
  description: string,
  category: MatchmakingTest["category"],
  testFn: () => Promise<MatchmakingTestResult>
): MatchmakingTest {
  return {
    name,
    description,
    category,
    run: async () => {
      const startTime = performance.now();
      // Track users created during this specific test
      const usersBeforeTest = [...testUsersToCleanup];

      try {
        const result = await testFn();

        // Cleanup users created during this test
        const usersToClean = testUsersToCleanup.filter(u => !usersBeforeTest.includes(u));
        const cleanedCount = await cleanupTestUsers(usersToClean);
        // Remove cleaned users from global tracking
        usersToClean.forEach(u => {
          const idx = testUsersToCleanup.indexOf(u);
          if (idx > -1) testUsersToCleanup.splice(idx, 1);
        });

        return {
          ...result,
          duration: performance.now() - startTime,
          details: {
            ...result.details,
            _testAccountsCleaned: cleanedCount,
          },
        };
      } catch (error) {
        // Still cleanup on error
        const usersToClean = testUsersToCleanup.filter(u => !usersBeforeTest.includes(u));
        await cleanupTestUsers(usersToClean);
        usersToClean.forEach(u => {
          const idx = testUsersToCleanup.indexOf(u);
          if (idx > -1) testUsersToCleanup.splice(idx, 1);
        });

        if (error instanceof AssertionError) {
          return {
            passed: false,
            message: error.message,
            details: {
              expected: error.expected,
              actual: error.actual,
            },
            duration: performance.now() - startTime,
          };
        }
        return {
          passed: false,
          message: `Unexpected error: ${error instanceof Error ? error.message : String(error)}`,
          details: {
            error: error instanceof Error ? error.stack : String(error),
          },
          duration: performance.now() - startTime,
        };
      }
    },
  };
}

// ============================================================================
// PLACEMENT MATCH TESTS
// ============================================================================

const placementTests: MatchmakingTest[] = [
  createTest(
    "Placement win updates rating correctly",
    "A placement win should increase provisional rating with detailed calculations",
    "placement",
    async () => {
      const lightId = generateTestUserId();
      const darkId = generateTestUserId();
      const gameId = `test_game_${Date.now()}`;

      // Initialize both users
      const lightProfile = await MMRService.initializeUserMMR(createMockUser(lightId, "TestLight"));
      const darkProfile = await MMRService.initializeUserMMR(createMockUser(darkId, "TestDark"));

      assertDefined(lightProfile, "Light profile should be created");
      assertDefined(darkProfile, "Dark profile should be created");

      const lightRatingBefore = lightProfile.mmr.provisionalRating;
      const darkRatingBefore = darkProfile.mmr.provisionalRating;

      // Calculate expected change BEFORE the game
      const gameNumber = 1;
      const expectedChange = calculateExpectedPlacementChange(
        lightRatingBefore,
        darkRatingBefore,
        "win",
        gameNumber
      );
      const winProbability = calculateWinProbability(lightRatingBefore, darkRatingBefore);

      // Process a win for light player
      const result = await MMRService.processGameResult(
        gameId,
        lightId,
        darkId,
        "light",
        "competitive",
        "standard"
      );

      assertDefined(result, "Game result should be returned");
      assertDefined(result.lightPlayerResult, "Light player result should exist");

      // Verify rating increased
      assertTrue(
        result.lightPlayerResult.ratingChange > 0,
        `Expected positive rating change for winner, got ${result.lightPlayerResult.ratingChange}`
      );

      // Verify placement game was recorded
      assertEqual(
        result.lightPlayerResult.placementGamesCompleted,
        1,
        "Should have 1 placement game completed"
      );

      return passTest("Placement win correctly increased rating", {
        // Rating progression
        ratingBefore: lightRatingBefore,
        ratingAfter: result.lightPlayerResult.ratingAfter,
        actualChange: formatChange(result.lightPlayerResult.ratingChange),
        expectedChange: formatChange(expectedChange),
        changeMatch: result.lightPlayerResult.ratingChange === expectedChange,
        // Calculation details
        opponentRating: darkRatingBefore,
        winProbability: `${(winProbability * 100).toFixed(1)}%`,
        gameNumber: gameNumber,
        volatility: Math.max(100 - (gameNumber - 1) * 6, 40),
        // Placement status
        placementProgress: `${result.lightPlayerResult.placementGamesCompleted}/${PLACEMENT_GAMES}`,
        isPlaced: false,
      });
    }
  ),

  createTest(
    "Placement loss updates rating correctly",
    "A placement loss should decrease provisional rating with detailed calculations",
    "placement",
    async () => {
      const lightId = generateTestUserId();
      const darkId = generateTestUserId();
      const gameId = `test_game_${Date.now()}`;

      // Initialize both users
      const lightProfile = await MMRService.initializeUserMMR(createMockUser(lightId, "TestLight"));
      const darkProfile = await MMRService.initializeUserMMR(createMockUser(darkId, "TestDark"));

      const lightRatingBefore = lightProfile.mmr.provisionalRating;
      const darkRatingBefore = darkProfile.mmr.provisionalRating;

      // Calculate expected changes
      const gameNumber = 1;
      const expectedLightChange = calculateExpectedPlacementChange(
        lightRatingBefore,
        darkRatingBefore,
        "loss",
        gameNumber
      );
      const expectedDarkChange = calculateExpectedPlacementChange(
        darkRatingBefore,
        lightRatingBefore,
        "win",
        gameNumber
      );

      // Process a loss for light player (dark wins)
      const result = await MMRService.processGameResult(
        gameId,
        lightId,
        darkId,
        "dark",
        "competitive",
        "standard"
      );

      assertDefined(result, "Game result should be returned");
      assertDefined(result.lightPlayerResult, "Light player result should exist");

      // Verify rating decreased for loser
      assertTrue(
        result.lightPlayerResult.ratingChange < 0,
        `Expected negative rating change for loser, got ${result.lightPlayerResult.ratingChange}`
      );

      // Verify winner gained rating
      assertTrue(
        result.darkPlayerResult.ratingChange > 0,
        `Expected positive rating change for winner, got ${result.darkPlayerResult.ratingChange}`
      );

      return passTest("Placement loss correctly decreased rating", {
        // Light player (loser)
        lightRatingBefore,
        lightRatingAfter: result.lightPlayerResult.ratingAfter,
        lightActualChange: formatChange(result.lightPlayerResult.ratingChange),
        lightExpectedChange: formatChange(expectedLightChange),
        // Dark player (winner)
        darkRatingBefore,
        darkRatingAfter: result.darkPlayerResult.ratingAfter,
        darkActualChange: formatChange(result.darkPlayerResult.ratingChange),
        darkExpectedChange: formatChange(expectedDarkChange),
        // Calculation details
        gameNumber,
        volatility: Math.max(100 - (gameNumber - 1) * 6, 40),
      });
    }
  ),

  createTest(
    "10-game placement journey (realistic matchmaking)",
    "Placement with dynamic opponent ratings matching player's provisional (simulates real matchmaking)",
    "placement",
    async () => {
      const playerId = generateTestUserId();
      const opponentId = generateTestUserId();

      // Initialize player (in placement)
      const initialPlayerProfile = await MMRService.initializeUserMMR(createMockUser(playerId, "TestPlayer"));

      // Initialize opponent as a PLACED player at 1300
      // This simulates matchmaking finding "equal skill" opponents
      await MMRService.initializeUserMMR(createMockUser(opponentId, "Opponent"));

      // Manually mark opponent as placed so they have stable K-factor
      const opponentProfile = await MMRService.getUserProfile(opponentId);
      assertDefined(opponentProfile, "Opponent profile should exist");
      opponentProfile.mmr.isPlaced = true;
      opponentProfile.mmr.displayRating = START_RATING;
      opponentProfile.mmr.placementGames = PLACEMENT_GAMES;
      if (opponentProfile.mmr.timeControlRatings?.standard) {
        opponentProfile.mmr.timeControlRatings.standard.isPlaced = true;
        opponentProfile.mmr.timeControlRatings.standard.rating = START_RATING;
        opponentProfile.mmr.timeControlRatings.standard.placementGames = PLACEMENT_GAMES;
      }
      await MMRService.updateUserProfile(opponentId, opponentProfile);

      // Track all games
      const gameHistory: DetailedGameResult[] = [];
      let wins = 0;
      let losses = 0;

      // Play 10 games with RANDOM outcomes
      for (let i = 0; i < PLACEMENT_GAMES; i++) {
        const gameId = `test_game_${Date.now()}_${i}`;
        const gameNumber = i + 1;

        // Get player's current provisional rating
        const playerProfile = await MMRService.getUserProfile(playerId);
        assertDefined(playerProfile, "Player profile should exist");
        const ratingBefore = playerProfile.mmr.provisionalRating;

        // Set opponent's rating to match player's provisional rating
        // This simulates matchmaking finding an "equal skill" opponent each game
        const currentOpponent = await MMRService.getUserProfile(opponentId);
        assertDefined(currentOpponent, "Opponent profile should exist");
        const targetOpponentRating = ratingBefore;
        currentOpponent.mmr.displayRating = targetOpponentRating;
        if (currentOpponent.mmr.timeControlRatings?.standard) {
          currentOpponent.mmr.timeControlRatings.standard.rating = targetOpponentRating;
        }
        await MMRService.updateUserProfile(opponentId, currentOpponent);

        // Random outcome (player is always light)
        const winner = randomOutcome();
        const playerWins = winner === "light";
        const outcome: "win" | "loss" = playerWins ? "win" : "loss";

        if (playerWins) wins++;
        else losses++;

        // Process the game
        const result = await MMRService.processGameResult(
          gameId,
          playerId,
          opponentId,
          winner,
          "competitive",
          "standard"
        );

        assertDefined(result, "Game result should be returned");

        const actualChange = result.lightPlayerResult.ratingChange;

        // Record detailed result
        gameHistory.push({
          gameNumber,
          outcome,
          ratingBefore,
          ratingAfter: result.lightPlayerResult.ratingAfter,
          change: actualChange,
          expectedChange: 0, // Not checking expected - opponent state is complex
          opponentRating: targetOpponentRating,
          placementProgress: `${gameNumber}/${PLACEMENT_GAMES}`,
          isPlaced: gameNumber >= PLACEMENT_GAMES,
        });

        // Small delay to avoid timestamp collisions
        await new Promise((r) => setTimeout(r, 10));
      }

      // Fetch final profile to verify placement status
      const finalProfile = await MMRService.getUserProfile(playerId);
      assertDefined(finalProfile, "Final profile should exist");

      // Build game-by-game breakdown (simplified - no expected column)
      const gameBreakdown = gameHistory.map((g) => ({
        game: g.gameNumber,
        result: g.outcome.toUpperCase(),
        rating: `${g.ratingBefore} → ${g.ratingAfter}`,
        change: formatChange(g.change),
        vsRating: g.opponentRating,
      }));

      // Build the complete details object
      const testDetails = {
        // Summary
        finalRating: finalProfile.mmr.displayRating,
        startRating: START_RATING,
        netChange: formatChange(finalProfile.mmr.displayRating - START_RATING),
        winLossRecord: `${wins}W-${losses}L`,
        winRate: `${((wins / PLACEMENT_GAMES) * 100).toFixed(0)}%`,
        // Placement status from Firebase
        isPlaced: finalProfile.mmr.isPlaced,
        placementGamesCompleted: finalProfile.mmr.placementGames,
        // Firebase profile snapshot
        firebaseProfile: {
          provisionalRating: finalProfile.mmr.provisionalRating,
          displayRating: finalProfile.mmr.displayRating,
          placementGames: finalProfile.mmr.placementGames,
          totalGames: finalProfile.mmr.totalGames,
          isPlaced: finalProfile.mmr.isPlaced,
          colorBalance: finalProfile.mmr.colorBalance,
        },
        // Initial profile for comparison
        initialProfile: {
          provisionalRating: initialPlayerProfile?.mmr.provisionalRating,
          isPlaced: initialPlayerProfile?.mmr.isPlaced,
          placementGames: initialPlayerProfile?.mmr.placementGames,
        },
        // Game-by-game breakdown
        games: gameBreakdown,
      };

      // FAIL if placement didn't complete
      if (!finalProfile.mmr.isPlaced) {
        return failTest(
          `Player should be placed after 10 games but isPlaced=${finalProfile.mmr.isPlaced}`,
          { isPlaced: true, placementGames: PLACEMENT_GAMES },
          testDetails
        );
      }

      // Verify rating moved in expected direction based on win/loss ratio
      const expectedDirection = wins > losses ? "up" : wins < losses ? "down" : "stable";
      const actualDirection = finalProfile.mmr.displayRating > START_RATING ? "up" :
                              finalProfile.mmr.displayRating < START_RATING ? "down" : "stable";

      if (expectedDirection !== actualDirection && expectedDirection !== "stable") {
        return failTest(
          `Rating moved ${actualDirection} but expected ${expectedDirection} with ${wins}W-${losses}L`,
          { expectedDirection },
          testDetails
        );
      }

      return passTest(`Completed 10-game placement: ${wins}W-${losses}L`, testDetails);
    }
  ),

  createTest(
    "Placement rating reflects performance",
    "Rating after placement should be higher for more wins",
    "placement",
    async () => {
      // Test with all wins - should get high rating
      const winnerId = generateTestUserId();
      const loserId = generateTestUserId();

      await MMRService.initializeUserMMR(createMockUser(winnerId, "Winner"));
      await MMRService.initializeUserMMR(createMockUser(loserId, "Loser"));

      // Winner wins all 10 games
      for (let i = 0; i < PLACEMENT_GAMES; i++) {
        await MMRService.processGameResult(
          `test_${Date.now()}_${i}`,
          winnerId,
          loserId,
          "light",
          "competitive",
          "standard"
        );
      }

      const winnerProfile = await MMRService.getUserProfile(winnerId);
      const loserProfile = await MMRService.getUserProfile(loserId);

      assertDefined(winnerProfile, "Winner profile should exist");
      assertDefined(loserProfile, "Loser profile should exist");

      // Winner should have higher rating than loser
      assertTrue(
        winnerProfile.mmr.displayRating > loserProfile.mmr.displayRating,
        `Winner (${winnerProfile.mmr.displayRating}) should have higher rating than loser (${loserProfile.mmr.displayRating})`
      );

      // Winner should be above starting rating
      assertTrue(
        winnerProfile.mmr.displayRating > START_RATING,
        `Winner should be above ${START_RATING}, got ${winnerProfile.mmr.displayRating}`
      );

      return passTest("Rating calculation correctly reflects performance", {
        winnerRating: winnerProfile.mmr.displayRating,
        loserRating: loserProfile.mmr.displayRating,
        startRating: START_RATING,
      });
    }
  ),
];

// ============================================================================
// ACCOUNT TESTS
// ============================================================================

const accountTests: MatchmakingTest[] = [
  createTest(
    "New user gets initialized profile",
    "Creating a new user should initialize MMR data correctly",
    "account",
    async () => {
      const userId = generateTestUserId();

      const profile = await MMRService.initializeUserMMR(createMockUser(userId, "NewUser"));

      assertDefined(profile, "Profile should be created");
      assertEqual(profile.mmr.provisionalRating, START_RATING, "Should start at default rating");
      assertEqual(profile.mmr.placementGames, 0, "Should have 0 placement games");
      assertEqual(profile.mmr.isPlaced, false, "Should not be placed yet");
      assertEqual(profile.mmr.totalGames, 0, "Should have 0 total games");

      return passTest("New user profile initialized correctly", {
        provisionalRating: profile.mmr.provisionalRating,
        placementGames: profile.mmr.placementGames,
        isPlaced: profile.mmr.isPlaced,
      });
    }
  ),

  createTest(
    "Color balance updates correctly",
    "Playing as light/dark should update color balance",
    "account",
    async () => {
      const playerId = generateTestUserId();
      const opponentId = generateTestUserId();

      await MMRService.initializeUserMMR(createMockUser(playerId, "Player"));
      await MMRService.initializeUserMMR(createMockUser(opponentId, "Opponent"));

      // Play as light
      await MMRService.processGameResult(
        `color_test_1_${Date.now()}`,
        playerId,
        opponentId,
        "light",
        "competitive",
        "standard"
      );

      let profile = await MMRService.getUserProfile(playerId);
      assertEqual(profile?.mmr.colorBalance, 1, "Color balance should be +1 after playing light");

      // Play as dark
      await MMRService.processGameResult(
        `color_test_2_${Date.now()}`,
        opponentId,
        playerId, // player is now dark
        "light",
        "competitive",
        "standard"
      );

      profile = await MMRService.getUserProfile(playerId);
      assertEqual(profile?.mmr.colorBalance, 0, "Color balance should be 0 after playing both colors");

      return passTest("Color balance updates correctly", {
        finalColorBalance: profile?.mmr.colorBalance,
      });
    }
  ),

  createTest(
    "Game history is recorded",
    "Each game should be recorded in player's game history",
    "account",
    async () => {
      const playerId = generateTestUserId();
      const opponentId = generateTestUserId();

      await MMRService.initializeUserMMR(createMockUser(playerId, "Player"));
      await MMRService.initializeUserMMR(createMockUser(opponentId, "Opponent"));

      const gameId = `history_test_${Date.now()}`;

      const result = await MMRService.processGameResult(
        gameId,
        playerId,
        opponentId,
        "light",
        "competitive",
        "standard"
      );

      assertDefined(result, "Result should exist");
      assertEqual(result.lightPlayerResult.gameId, gameId, "Game ID should be recorded");
      assertEqual(
        result.lightPlayerResult.result,
        "win",
        "Light player result should be 'win'"
      );
      assertEqual(
        result.darkPlayerResult.result,
        "loss",
        "Dark player result should be 'loss'"
      );

      return passTest("Game history correctly recorded", {
        gameId,
        lightResult: result.lightPlayerResult.result,
        darkResult: result.darkPlayerResult.result,
      });
    }
  ),
];

// ============================================================================
// RATING TESTS
// ============================================================================

const ratingTests: MatchmakingTest[] = [
  createTest(
    "Casual games don't affect rating",
    "Games in casual mode should not change ELO",
    "rating",
    async () => {
      const lightId = generateTestUserId();
      const darkId = generateTestUserId();

      // Create players
      const lightProfile = await MMRService.initializeUserMMR(createMockUser(lightId, "Light"));
      await MMRService.initializeUserMMR(createMockUser(darkId, "Dark"));

      const ratingBefore = lightProfile.mmr.provisionalRating;

      // Process a CASUAL game
      const result = await MMRService.processGameResult(
        `casual_${Date.now()}`,
        lightId,
        darkId,
        "light",
        "casual", // CASUAL mode
        "standard"
      );

      // Casual games should not change rating
      if (result) {
        assertEqual(
          result.lightPlayerResult.ratingChange,
          0,
          "Casual winner should have 0 rating change"
        );
        assertEqual(
          result.darkPlayerResult.ratingChange,
          0,
          "Casual loser should have 0 rating change"
        );
      }

      // Verify profile wasn't updated
      const finalProfile = await MMRService.getUserProfile(lightId);
      assertEqual(
        finalProfile?.mmr.provisionalRating,
        ratingBefore,
        "Rating should be unchanged after casual game"
      );

      return passTest("Casual games correctly skip rating changes", {
        ratingBefore,
        ratingAfter: finalProfile?.mmr.provisionalRating,
      });
    }
  ),
];

// ============================================================================
// DRAW TESTS
// ============================================================================

const drawTests: MatchmakingTest[] = [
  createTest(
    "Draw results in minimal rating change",
    "A draw should result in minimal or no rating change for equal-rated players",
    "rating",
    async () => {
      const lightId = generateTestUserId();
      const darkId = generateTestUserId();

      // Create players
      await MMRService.initializeUserMMR(createMockUser(lightId, "Light"));
      await MMRService.initializeUserMMR(createMockUser(darkId, "Dark"));

      // Process a draw
      const result = await MMRService.processGameResult(
        `draw_${Date.now()}`,
        lightId,
        darkId,
        "draw",
        "competitive",
        "standard"
      );

      assertDefined(result, "Result should exist");

      // For equal ratings during placement, draw should have some change
      // but it should be symmetric
      const lightChange = result.lightPlayerResult.ratingChange;
      const darkChange = result.darkPlayerResult.ratingChange;

      // Changes should be relatively small and opposite
      assertInRange(
        Math.abs(lightChange),
        0,
        50,
        "Draw rating change should be moderate"
      );

      return passTest("Draw correctly handled", {
        lightChange,
        darkChange,
      });
    }
  ),
];

// ============================================================================
// CLOCK/TIMER TESTS
// ============================================================================

// Import TimeControlManager for timer tests
import TimeControlManager from "../../timeControl/TimeControlManager";
import { TIME_PRESETS, formatTime } from "../../timeControl/presets";
import { TimeControlPreset } from "../../timeControl/types";

/**
 * Format milliseconds as human-readable time string
 */
function formatMs(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')} (${ms}ms)`;
}

/**
 * Get preset display name
 */
function getPresetName(preset: TimeControlPreset): string {
  const names: Record<TimeControlPreset, string> = {
    [TimeControlPreset.OFF]: 'Off',
    [TimeControlPreset.SPRINT]: 'Sprint (1 min)',
    [TimeControlPreset.STANDARD]: 'Standard (3 min)',
    [TimeControlPreset.MARATHON]: 'Marathon (10 min)',
    [TimeControlPreset.ANY]: 'Any',
  };
  return names[preset];
}

const clockTests: MatchmakingTest[] = [
  createTest(
    "Timer resets to full time on new game",
    "Starting a new game after one ended with reduced time should reset to full initial time",
    "clock",
    async () => {
      const standardConfig = TIME_PRESETS[TimeControlPreset.STANDARD];
      const initialTime = standardConfig.initialTime; // 180000ms (3 minutes)

      // === GAME 1: Play until time is reduced ===
      TimeControlManager.initialize(standardConfig, 'local');

      // Verify initial time
      const game1LightStart = TimeControlManager.getTimeRemaining('light');
      const game1DarkStart = TimeControlManager.getTimeRemaining('dark');

      if (game1LightStart !== initialTime || game1DarkStart !== initialTime) {
        return failTest("Game 1 should start with full time", {
          expected: { light: initialTime, dark: initialTime },
          actual: { light: game1LightStart, dark: game1DarkStart },
        });
      }

      // Start timer and deduct time
      TimeControlManager.startTimer('light');
      const lightDeduction = 60000;
      TimeControlManager.deductTime('light', lightDeduction); // Deduct 60 seconds

      const lightAfterDeduct = TimeControlManager.getTimeRemaining('light');
      if (lightAfterDeduct !== 120000) {
        return failTest("Light should have 120s after 60s deduction", {
          expected: 120000,
          actual: lightAfterDeduct,
        });
      }

      // Turn change - light gets increment, dark starts
      TimeControlManager.handleTurnChange('light', 'dark');
      const darkDeduction = 90000;
      TimeControlManager.deductTime('dark', darkDeduction); // Deduct 90 seconds from dark

      // End game 1
      TimeControlManager.stopOnGameEnd();

      const game1EndLightTime = TimeControlManager.getTimeRemaining('light');
      const game1EndDarkTime = TimeControlManager.getTimeRemaining('dark');

      // === GAME 2: Start new game - timers MUST reset ===
      TimeControlManager.initialize(standardConfig, 'local');

      const game2LightStart = TimeControlManager.getTimeRemaining('light');
      const game2DarkStart = TimeControlManager.getTimeRemaining('dark');

      // CRITICAL: Both must be at full initial time
      if (game2LightStart !== initialTime) {
        return failTest("Light timer did not reset to full time for new game", {
          expected: initialTime,
          actual: game2LightStart,
          game1EndTime: game1EndLightTime,
        });
      }

      if (game2DarkStart !== initialTime) {
        return failTest("Dark timer did not reset to full time for new game", {
          expected: initialTime,
          actual: game2DarkStart,
          game1EndTime: game1EndDarkTime,
        });
      }

      // Verify clean state
      const state = TimeControlManager.getState();
      if (state.timeExpired || state.winner) {
        return failTest("Game state was not properly reset", {
          expected: { timeExpired: false, winner: undefined },
          actual: { timeExpired: state.timeExpired, winner: state.winner },
        });
      }

      return passTest("Timer correctly resets to full time on new game", {
        clockTestData: {
          preset: getPresetName(TimeControlPreset.STANDARD),
          initialTime: formatMs(initialTime),
          increment: `${standardConfig.increment / 1000}s`,
          game1: {
            title: "Game 1 (Ended with reduced time)",
            lightDeducted: formatMs(lightDeduction),
            darkDeducted: formatMs(darkDeduction),
            lightEndTime: formatMs(game1EndLightTime),
            darkEndTime: formatMs(game1EndDarkTime),
          },
          game2: {
            title: "Game 2 (After reset)",
            lightStartTime: formatMs(game2LightStart),
            darkStartTime: formatMs(game2DarkStart),
            resetCorrect: game2LightStart === initialTime && game2DarkStart === initialTime,
          },
        },
      });
    }
  ),

  createTest(
    "Timer resets after timeout loss",
    "Starting a new game after previous game ended with timeout should have full time",
    "clock",
    async () => {
      const sprintConfig = TIME_PRESETS[TimeControlPreset.SPRINT];
      const initialTime = sprintConfig.initialTime; // 60000ms (1 minute)

      // === GAME 1: Light runs out of time ===
      TimeControlManager.initialize(sprintConfig, 'local');
      TimeControlManager.startTimer('light');
      TimeControlManager.deductTime('light', 60000); // All time gone

      const lightAfterTimeout = TimeControlManager.getTimeRemaining('light');
      const state1 = TimeControlManager.getState();

      if (!state1.timeExpired) {
        return failTest("Game should detect time expiration", {
          expected: { timeExpired: true },
          actual: { timeExpired: state1.timeExpired, lightTime: lightAfterTimeout },
        });
      }

      // === GAME 2: Start new game ===
      TimeControlManager.initialize(sprintConfig, 'local');

      const game2LightTime = TimeControlManager.getTimeRemaining('light');
      const game2DarkTime = TimeControlManager.getTimeRemaining('dark');
      const state2 = TimeControlManager.getState();

      if (game2LightTime !== initialTime) {
        return failTest("Light timer not reset after timeout", {
          expected: initialTime,
          actual: game2LightTime,
        });
      }

      if (state2.timeExpired) {
        return failTest("timeExpired flag should be reset", {
          expected: false,
          actual: state2.timeExpired,
        });
      }

      return passTest("Timer correctly resets after timeout loss", {
        clockTestData: {
          preset: getPresetName(TimeControlPreset.SPRINT),
          initialTime: formatMs(initialTime),
          increment: `${sprintConfig.increment / 1000}s`,
          game1: {
            title: "Game 1 (Timeout)",
            lightTimeAtEnd: formatMs(lightAfterTimeout),
            timeExpired: true,
            winner: state1.winner || "dark",
          },
          game2: {
            title: "Game 2 (After timeout reset)",
            lightStartTime: formatMs(game2LightTime),
            darkStartTime: formatMs(game2DarkTime),
            timeExpiredFlag: state2.timeExpired,
            resetCorrect: game2LightTime === initialTime,
          },
        },
      });
    }
  ),

  createTest(
    "Timer resets when switching presets",
    "Changing from Sprint to Marathon should use new preset's initial time",
    "clock",
    async () => {
      const sprintConfig = TIME_PRESETS[TimeControlPreset.SPRINT];
      const marathonConfig = TIME_PRESETS[TimeControlPreset.MARATHON];

      // Start Sprint game and use some time
      TimeControlManager.initialize(sprintConfig, 'local');
      TimeControlManager.startTimer('light');
      const sprintDeduction = 30000;
      TimeControlManager.deductTime('light', sprintDeduction);
      TimeControlManager.stopOnGameEnd();

      const sprintEndTime = TimeControlManager.getTimeRemaining('light');

      // Switch to Marathon
      TimeControlManager.initialize(marathonConfig, 'local');

      const marathonStartLight = TimeControlManager.getTimeRemaining('light');
      const marathonStartDark = TimeControlManager.getTimeRemaining('dark');

      if (marathonStartLight !== marathonConfig.initialTime) {
        return failTest("Light timer should be Marathon initial time", {
          expected: marathonConfig.initialTime,
          actual: marathonStartLight,
          previousSprintTime: sprintEndTime,
        });
      }

      if (marathonStartDark !== marathonConfig.initialTime) {
        return failTest("Dark timer should be Marathon initial time", {
          expected: marathonConfig.initialTime,
          actual: marathonStartDark,
        });
      }

      return passTest("Timer correctly updates when switching presets", {
        clockTestData: {
          game1: {
            title: "Game 1 - Sprint",
            preset: getPresetName(TimeControlPreset.SPRINT),
            initialTime: formatMs(sprintConfig.initialTime),
            deducted: formatMs(sprintDeduction),
            endTime: formatMs(sprintEndTime),
          },
          game2: {
            title: "Game 2 - Marathon (Preset Switch)",
            preset: getPresetName(TimeControlPreset.MARATHON),
            expectedInitialTime: formatMs(marathonConfig.initialTime),
            actualLightTime: formatMs(marathonStartLight),
            actualDarkTime: formatMs(marathonStartDark),
            resetCorrect: marathonStartLight === marathonConfig.initialTime,
          },
        },
      });
    }
  ),

  createTest(
    "Multiple rapid game restarts maintain correct time",
    "Starting many games in quick succession should always have correct initial time",
    "clock",
    async () => {
      const sprintConfig = TIME_PRESETS[TimeControlPreset.SPRINT];
      const initialTime = sprintConfig.initialTime;
      const iterations = 5;
      const gameResults: Array<{
        game: number;
        startTime: string;
        deducted: string;
        endTime: string;
        correct: string;
      }> = [];

      for (let i = 0; i < iterations; i++) {
        TimeControlManager.initialize(sprintConfig, 'local');
        const startTime = TimeControlManager.getTimeRemaining('light');
        const deduction = 10000 * (i + 1);

        // Use some time
        TimeControlManager.startTimer('light');
        TimeControlManager.deductTime('light', deduction);
        TimeControlManager.stopOnGameEnd();
        const endTime = TimeControlManager.getTimeRemaining('light');

        gameResults.push({
          game: i + 1,
          startTime: formatMs(startTime),
          deducted: formatMs(deduction),
          endTime: formatMs(endTime),
          correct: startTime === initialTime ? "Yes" : "No",
        });
      }

      // Final game
      TimeControlManager.initialize(sprintConfig, 'local');
      const finalLightTime = TimeControlManager.getTimeRemaining('light');
      const finalDarkTime = TimeControlManager.getTimeRemaining('dark');

      const allCorrect = gameResults.every(r => r.correct === "Yes") &&
                        finalLightTime === initialTime &&
                        finalDarkTime === initialTime;

      if (!allCorrect) {
        return failTest("Not all iterations had correct initial time", {
          expected: initialTime,
          gameResults,
          final: { light: finalLightTime, dark: finalDarkTime },
        });
      }

      return passTest(`All ${iterations} rapid restarts maintained correct time`, {
        clockTestData: {
          preset: getPresetName(TimeControlPreset.SPRINT),
          expectedInitialTime: formatMs(initialTime),
          iterations: iterations,
          gameResults,
          finalGame: {
            title: `Game ${iterations + 1} (Final verification)`,
            lightTime: formatMs(finalLightTime),
            darkTime: formatMs(finalDarkTime),
            allResetsCorrect: allCorrect,
          },
        },
      });
    }
  ),

  createTest(
    "Timer stops when game ends",
    "After stopOnGameEnd() is called, timer should be paused and not running",
    "clock",
    async () => {
      const standardConfig = TIME_PRESETS[TimeControlPreset.STANDARD];
      const initialTime = standardConfig.initialTime;

      // Initialize and start timer
      TimeControlManager.initialize(standardConfig, 'local');
      TimeControlManager.startTimer('light');

      // Verify timer is running
      const stateBeforeEnd = TimeControlManager.getState();
      const isRunningBefore = !stateBeforeEnd.playerTime.isPaused;
      const activePlayerBefore = stateBeforeEnd.playerTime.activePlayer;

      // End game
      TimeControlManager.stopOnGameEnd();

      // Verify timer is stopped
      const stateAfterEnd = TimeControlManager.getState();
      const isPausedAfter = stateAfterEnd.playerTime.isPaused;
      const activePlayerAfter = stateAfterEnd.playerTime.activePlayer;

      if (!isRunningBefore) {
        return failTest("Timer was not running before stopOnGameEnd", {
          expected: { isPaused: false, activePlayer: 'light' },
          actual: { isPaused: stateBeforeEnd.playerTime.isPaused, activePlayer: activePlayerBefore },
        });
      }

      if (!isPausedAfter) {
        return failTest("Timer should be paused after stopOnGameEnd", {
          expected: { isPaused: true },
          actual: { isPaused: isPausedAfter, activePlayer: activePlayerAfter },
        });
      }

      return passTest("Timer correctly stops when game ends", {
        clockTestData: {
          preset: getPresetName(TimeControlPreset.STANDARD),
          initialTime: formatMs(initialTime),
          beforeGameEnd: {
            title: "Timer State Before stopOnGameEnd()",
            isPaused: stateBeforeEnd.playerTime.isPaused,
            activePlayer: activePlayerBefore || "none",
            wasRunning: isRunningBefore ? "Yes" : "No",
          },
          afterGameEnd: {
            title: "Timer State After stopOnGameEnd()",
            isPaused: isPausedAfter,
            activePlayer: activePlayerAfter || "none",
            correctlyStopped: isPausedAfter ? "Yes" : "No",
          },
        },
      });
    }
  ),

  createTest(
    "Timeout triggers game end state",
    "When time expires, timeExpired flag should be set and winner determined",
    "clock",
    async () => {
      const sprintConfig = TIME_PRESETS[TimeControlPreset.SPRINT];
      const initialTime = sprintConfig.initialTime; // 60000ms

      // Initialize and start timer for light
      TimeControlManager.initialize(sprintConfig, 'local');
      TimeControlManager.startTimer('light');

      // State before timeout
      const stateBefore = TimeControlManager.getState();
      const lightTimeBefore = TimeControlManager.getTimeRemaining('light');

      // Deduct all time from light (simulate timeout)
      TimeControlManager.deductTime('light', initialTime + 1000); // Deduct more than available

      // State after timeout
      const stateAfter = TimeControlManager.getState();
      const lightTimeAfter = TimeControlManager.getTimeRemaining('light');

      // Verify timeout was detected
      if (!stateAfter.timeExpired) {
        return failTest("timeExpired should be true after time runs out", {
          expected: true,
          actual: stateAfter.timeExpired,
          lightTimeRemaining: lightTimeAfter,
        });
      }

      // Verify winner is correct (dark wins when light times out)
      if (stateAfter.winner !== 'dark') {
        return failTest("Winner should be dark when light times out", {
          expected: 'dark',
          actual: stateAfter.winner,
        });
      }

      // Verify light time is 0 or clamped
      if (lightTimeAfter > 0) {
        return failTest("Light time should be 0 after timeout", {
          expected: 0,
          actual: lightTimeAfter,
        });
      }

      return passTest("Timeout correctly triggers game end state", {
        clockTestData: {
          preset: getPresetName(TimeControlPreset.SPRINT),
          initialTime: formatMs(initialTime),
          beforeTimeout: {
            title: "State Before Timeout",
            lightTime: formatMs(lightTimeBefore),
            timeExpired: stateBefore.timeExpired ? "Yes" : "No",
            winner: stateBefore.winner || "none",
          },
          afterTimeout: {
            title: "State After Timeout",
            lightTime: formatMs(lightTimeAfter),
            timeExpired: stateAfter.timeExpired ? "Yes" : "No",
            winner: stateAfter.winner || "none",
            correctWinner: stateAfter.winner === 'dark' ? "Yes (Dark)" : "No",
          },
        },
      });
    }
  ),

  createTest(
    "ELO calculation on timeout (simulated)",
    "Verifies that timeout victory produces correct ELO calculation inputs",
    "clock",
    async () => {
      // This test verifies the flow that would lead to ELO adjustment
      // In real multiplayer, the TimeControlManager.subscribe() handler triggers checkGameEnd()
      // which sets gameState.state = PostGame and gameState.lightWon = winner === 'light'
      // Then the MMR useEffect picks it up and calls processGameResult()

      const sprintConfig = TIME_PRESETS[TimeControlPreset.SPRINT];

      // Simulate the timeout flow
      TimeControlManager.initialize(sprintConfig, 'local');
      TimeControlManager.startTimer('dark'); // Dark's turn
      TimeControlManager.deductTime('dark', sprintConfig.initialTime + 1000); // Dark times out

      const state = TimeControlManager.getState();

      // Verify the state that would be passed to game end
      const expectedWinner = 'light'; // Light wins when dark times out
      const expectedLightWon = true;

      if (!state.timeExpired) {
        return failTest("timeExpired should be true", {
          actual: state.timeExpired,
        });
      }

      if (state.winner !== expectedWinner) {
        return failTest("Winner should be light when dark times out", {
          expected: expectedWinner,
          actual: state.winner,
        });
      }

      // Simulate what checkGameEnd() would do
      const simulatedGameState = {
        state: 'PostGame',
        lightWon: state.winner === 'light',
        winCondition: 'timeout',
      };

      // Verify the simulated game state matches expected
      if (simulatedGameState.lightWon !== expectedLightWon) {
        return failTest("lightWon should be true when dark times out", {
          expected: expectedLightWon,
          actual: simulatedGameState.lightWon,
        });
      }

      return passTest("Timeout produces correct game end state for ELO processing", {
        clockTestData: {
          preset: getPresetName(TimeControlPreset.SPRINT),
          initialTime: formatMs(sprintConfig.initialTime),
          timeoutScenario: {
            title: "Dark Player Timeout Scenario",
            whoTimedOut: "Dark",
            timeExpired: state.timeExpired ? "Yes" : "No",
            winner: state.winner || "none",
          },
          gameEndState: {
            title: "Resulting Game State (for MMR)",
            state: simulatedGameState.state,
            lightWon: simulatedGameState.lightWon ? "Yes" : "No",
            winCondition: simulatedGameState.winCondition,
            eloWillProcess: simulatedGameState.state === 'PostGame' ? "Yes" : "No",
          },
        },
      });
    }
  ),
];

// ============================================================================
// EXPORT
// ============================================================================

export const matchmakingServiceTests: MatchmakingTest[] = [
  ...placementTests,
  ...accountTests,
  ...ratingTests,
  ...drawTests,
  ...clockTests,
];

export const matchmakingServiceSuite: MatchmakingTestSuite = {
  name: "Matchmaking",
  description: "Automated tests for matchmaking, ratings, and account management",
  tests: matchmakingServiceTests,
  isMatchmaking: true,
};

export const clockServiceSuite: MatchmakingTestSuite = {
  name: "Clock",
  description: "Timer reset and clock management tests",
  tests: clockTests,
  isMatchmaking: true,
};

// Empty exports for backwards compatibility
export const matchmakingTests: IntegrationTest[] = [];
export const matchmakingSuite: TestSuite = {
  name: "Matchmaking",
  description: "Automated tests for matchmaking, ratings, and account management",
  tests: matchmakingTests,
};
